import * as React from 'react';
import { Confirm, RaRecord, useCreate, useDelete, useGetList, useNotify, useRedirect } from 'react-admin';
import { preventNumberInputScroll } from '../../utils/preventNumberInputScroll';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Stack,
  TextField,
  FormControl,
  Select,
  CircularProgress,
  FormHelperText,
  InputLabel,
  Autocomplete,
  Chip,
  useMediaQuery,
  Tooltip,
  MenuItem} from '@mui/material';
import { PageHeader } from '../../components/page-header/PageHeader';
import { Switch } from '../../components/switch/Switch';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { IconButton } from '../../components/icon-button/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded';
import {
  VariantGroupDialog,
  VariantGroup,
  VariantGroupSaveOptions,
  VariantTemplate,
} from '../../components/dialogs/VariantGroupDialog';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragHandle, SORT_TRANSITION } from '../../components/tables/draggableList';
import { Language } from '../../enums/Language';
import { getBackendLanguage, getLanguageOptions } from '../../core/i18n';
import TranslateService from '../../services/translate/translateService';
import { RestaurantService } from '../../services/restaurant/restaurantService';
import {
  TranslationsSection,
  TranslationsMap,
  TranslationSnapshot,
  AiTranslationResult,
  FieldTranslation,
  buildTranslationPayload,
} from '../../components/translations/TranslationsSection';
import { watchTranslations } from '../../core/translations/translationStatus';
import axiosInstance from '../../core/axiosConfig';
import { WeekDay } from '../../enums/weekDay';
import { Unit } from '../../enums/UnitType';
import { RestaurantItemService } from '../../services/restaurantItem/restaurantItemService';
import { labelAboveSx } from '../../core/theme/formStyles';
import { ImageCropDialog } from '../../components/dialogs/ImageCropDialog';
import { CategoryAddDialog } from '../../components/dialogs/CategoryAddDialog';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import { FormBackButton } from '../../components/back-button/FormBackButton';
import { ScrollToFirstError } from '../../components/form/ScrollToFirstError';
import { PAGE_GUTTER } from '../../core/theme/layout';

// Matches the backend @Size(max=800) on CreateMenuItemRequest.description.
// Backend: @Size(max = 100) na name, @Size(max = 800) na description.
const DIETARY_RESTRICTIONS = [
  { key: 'spicy', labelKey: 'spicy' },
  { key: 'dairy_free', labelKey: 'dairyFree' },
  { key: 'gluten_free', labelKey: 'glutenFree' },
  { key: 'vegan', labelKey: 'vegan' },
  { key: 'vegetarian', labelKey: 'vegetarian' },
  { key: 'kosher', labelKey: 'kosher' },
  { key: 'halal', labelKey: 'halal' },
] as const;

/* Godzina z backendu (LocalTime) przychodzi jako "12:00:00" albo [12, 0] - pole
   type="time" chce "HH:mm". */
function toTimeInput(value: unknown): string | undefined {
  if (Array.isArray(value)) {
    const [h = 0, m = 0] = value as number[];
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
  return typeof value === 'string' && value.length >= 5 ? value.slice(0, 5) : undefined;
}

// Data (LocalDate) jako "2026-10-31" albo [2026, 10, 31] - pole type="date" chce "yyyy-MM-dd".
function toDateInput(value: unknown): string {
  if (Array.isArray(value)) {
    const [y, m, d] = value as number[];
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }
  return typeof value === 'string' ? value : '';
}

const NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 800;

/* `name` to skrot z enuma ("pcs", "fl oz") — zostaje tylko jako fallback, gdyby
   backend dorzucil jednostke, ktorej nie ma jeszcze w tlumaczeniach. Etykieta
   w selectcie idzie przez i18n (`enums.unit.*`), bo surowe skroty byly dla
   uzytkownika nieczytelne. Wartosc opcji to caly czas klucz enuma. */
const UNITS = Object.entries(Unit).map(([id, name]) => ({
  id,
  name,
}));

// Odcisk zawartosci grupy/szablonu do porownania "czy ta grupa jest juz zapisana
// jako szablon". Normalizujemy tak jak toTemplatePayload w dataProviderze
// (cena i rozmiar jako liczby, nienumeryczny rozmiar = brak), zeby "10" i "10.00"
// albo "" i null nie dawaly falszywej roznicy.
function variantContentSignature(g: Pick<VariantGroup, 'name' | 'isRequired' | 'isSingleChoice' | 'items'>): string {
  return JSON.stringify([
    (g.name || '').trim(),
    Boolean(g.isRequired),
    g.isSingleChoice !== false,
    g.items.map((it) => {
      const size = String(it.size ?? '').trim();
      return [
        (it.name || '').trim(),
        size !== '' && !Number.isNaN(Number(size)) ? Number(size) : null,
        Number(it.price),
      ];
    }),
  ]);
}

interface LinkedProduct {
  id: number;
  name: string;
}

// Backend przechowuje warianty jako płaską listę; "grupa wariantów" to projekcja
// UI po wspólnym polu `category` + regułach. Warianty bez kategorii (wszystkie
// istniejące dziś w menusso) zwijają się w jedną grupę "legacy" — kluczowe dla
// wstecznej kompatybilności, żeby stare produkty dalej dawały się edytować.
function groupVariants(variants: any[], makeKey: () => string): VariantGroup[] {
  const order: string[] = [];
  const byCategory = new Map<string, VariantGroup>();
  // BE zwraca warianty juz posortowane po displayOrder; sortujemy dla pewnosci
  // (stabilnie, z id jako tie-breakerem), zeby grupy byly w kolejnosci z panelu.
  const sorted = [...variants].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || (a.id ?? 0) - (b.id ?? 0),
  );

  for (const v of sorted) {
    const category = (v.category || '').trim();
    if (!byCategory.has(category)) {
      order.push(category);
      byCategory.set(category, {
        key: makeKey(),
        name: category,
        // Reguły są kopiowane na każdy wariant grupy — bierzemy je z pierwszego.
        isRequired: v.isRequired ?? false,
        isSingleChoice: v.isSingleChoice ?? true,
        items: [],
      });
    }
    byCategory.get(category)!.items.push({
      name: v.name || '',
      size: v.size != null ? String(v.size) : '',
      price: v.price != null ? String(v.price) : '',
    });
  }

  return order.map((c) => byCategory.get(c)!);
}

// Sama karta grupy zostaje bez zmian — dochodzi tylko uchwyt do przeciągania
// i lekkie przygaszenie/cień w trakcie drag, tak samo jak w liście kategorii/dań.
const SortableVariantGroupCard = ({
  group,
  onEdit,
  onDelete,
  onToggleTemplate,
  isSavedAsTemplate,
}: {
  group: VariantGroup;
  onEdit: (group: VariantGroup) => void;
  onDelete: (key: string) => void;
  onToggleTemplate: (group: VariantGroup) => void;
  isSavedAsTemplate: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: group.key,
    transition: SORT_TRANSITION,
  });

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform) }}
      sx={{
        width: '100%',
        border: '1px solid var(--color-secondary)',
        borderRadius: 'var(--radius-xs)',
        p: 2,
        transition: `${SORT_TRANSITION.duration}ms ${SORT_TRANSITION.easing}`,
        ...(isDragging
          ? { opacity: 0.5, boxShadow: '0 14px 28px -6px rgba(0,0,0,0.22), 0 4px 10px rgba(0,0,0,0.12)' }
          : {}),
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <DragHandle listeners={listeners} attributes={attributes} />
          <Typography sx={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>
            {group.name || t('locationsManagement.createItem.variantGroupDialog.legacyGroupName')}
          </Typography>
          <Chip
            size="small"
            label={
              group.isSingleChoice
                ? t('locationsManagement.createItem.variantGroupDialog.singleChoice')
                : t('locationsManagement.createItem.variantGroupDialog.multipleChoice')
            }
          />
          {group.isRequired && (
            <Chip
              size="small"
              label={t('locationsManagement.createItem.variantGroupDialog.required')}
              sx={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}
            />
          )}
        </Stack>
        <Stack direction="row" spacing={0.5}>
          <Tooltip
            title={isSavedAsTemplate ? t('variantTemplates.removeTemplateAction') : t('variantTemplates.saveAsTemplateAction')}
          >
            <span>
              <IconButton
                icon={
                  isSavedAsTemplate ? (
                    <BookmarkAddedIcon fontSize="small" />
                  ) : (
                    <BookmarkAddOutlinedIcon fontSize="small" />
                  )
                }
                ariaLabel={
                  isSavedAsTemplate ? t('variantTemplates.removeTemplateAction') : t('variantTemplates.saveAsTemplateAction')
                }
                // Przelacznik: zapisana grupa po kliknieciu jest usuwana z szablonow.
                onClick={() => onToggleTemplate(group)}
              />
            </span>
          </Tooltip>
          <IconButton
            icon={<EditIcon fontSize="small" />}
            ariaLabel={t('common.edit', { defaultValue: 'Edit' })}
            onClick={() => onEdit(group)}
          />
          <IconButton
            icon={<DeleteOutlineIcon fontSize="small" />}
            ariaLabel={t('common.delete')}
            color="var(--color-error)"
            onClick={() => onDelete(group.key)}
          />
        </Stack>
      </Stack>

      {!group.name && (
        <Typography sx={{ fontSize: 'var(--text-2xs)', color: 'var(--color-error)', mb: 1 }}>
          {t('locationsManagement.createItem.variantGroupDialog.legacyGroupHint')}
        </Typography>
      )}

      <Stack spacing={0.5}>
        {group.items.map((it, i) => (
          <Stack key={i} direction="row" justifyContent="space-between" sx={{ color: 'text.secondary' }}>
            <Typography sx={{ fontSize: 'var(--text-xs)' }}>
              {it.name}
              {it.size ? ` · ${it.size}` : ''}
            </Typography>
            <Typography sx={{ fontSize: 'var(--text-xs)' }}>{it.price}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export default function LocationsCreateItem(): JSX.Element {
  const { t } = useTranslation();

  // Backend language code (ENG/POL/...) derived from the language currently
  // selected in the UI. Recomputed on every render, so it stays in sync when
  // the user switches the navbar language.
  const backendLanguage = getBackendLanguage();

  const LANGUAGE = getLanguageOptions(t);
  const translateService = new TranslateService();
  const [autoTranslate, setAutoTranslate] = React.useState(true);
  const [translations, setTranslations] = React.useState<TranslationsMap>({});
  const [lastSnapshot, setLastSnapshot] = React.useState<TranslationSnapshot | null>(null);
  /* Produkt nie ma wlasnego jezyka — backend tlumaczy go zawsze z jezyka
     lokalizacji (TranslateService.getLocationDefaultLanguage). */
  const [locationLanguage, setLocationLanguage] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);
  const [categories, setCategories] = React.useState<any[]>([]);
  const [categoriesLoaded, setCategoriesLoaded] = React.useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = React.useState(false);
  const [groups, setGroups] = React.useState<VariantGroup[]>([]);
  const [variantDialogOpen, setVariantDialogOpen] = React.useState(false);
  const [editingGroup, setEditingGroup] = React.useState<VariantGroup | null>(null);
  const groupKeyRef = React.useRef(0);
  const makeGroupKey = () => `g${++groupKeyRef.current}`;
  const [isLoading, setIsLoading] = React.useState(false);
  const [data, setData] = React.useState<RaRecord | null>(null);
  const [attemptedSubmit, setAttemptedSubmit] = React.useState(false);

  const [fileBlob, setFileBlob] = React.useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [cropDialogOpen, setCropDialogOpen] = React.useState(false);
  const [rawImageSrc, setRawImageSrc] = React.useState<string | null>(null);

  /* Jezyk, w ktorym pobierana jest lista powiazanych produktow. Startuje na jezyku UI,
     a po wczytaniu lokalizacji przechodzi na jej jezyk — w nim sa nazwa i opis. */
  const [formLanguage, setFormLanguage] = React.useState(backendLanguage);

  const [availableProducts, setAvailableProducts] = React.useState<LinkedProduct[]>([]);
  const [linkedProducts, setLinkedProducts] = React.useState<LinkedProduct[]>([]);
  const [productsLoading, setProductsLoading] = React.useState(false);
  const notify = useNotify();
  const redirect = useRedirect();
  const isMobile = useMediaQuery('(max-width:600px)');
  const { id, itemId } = useParams();
  const isEditMode = Boolean(itemId);
  const restaurantItemService = new RestaurantItemService();

  // Jezyk tlumaczenia przychodzi jako id ("ENG") albo jako wartosc enuma
  // zwracana przez endpoint tlumaczen ("English").
  const resolveLanguageId = (translationLanguage: string) =>
    LANGUAGE.find(
      (l) =>
        translationLanguage === l.id ||
        translationLanguage === l.name ||
        translationLanguage === Language[l.id as keyof typeof Language],
    )?.id;

  /**
   * Tlumaczenie z backendu w formie, jakiej uzywa modal. `source` musi przetrwac
   * przeladowanie strony — inaczej reczna poprawka wrocilaby jako AI i pierwszy
   * zapis oddalby ja automatowi do nadpisania.
   */
  const toFieldTranslation = (text?: string | null, source?: string): FieldTranslation | undefined => {
    if (!text) return undefined;
    // `aiText` to punkt powrotu dla przycisku "przywroc wersje AI" — przy tekscie
    // wpisanym recznie nie znamy wersji AI, wiec zostaje pusty.
    return source === 'MANUAL' ? { text, source: 'manual' } : { text, source: 'ai', aiText: text };
  };

  React.useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const location = await new RestaurantService().getRestaurants(Number(id));
        if (location?.language) {
          setLocationLanguage(location.language);
          setFormLanguage(location.language);
        }
      } catch (e) {
        console.error('Failed to load location language', e);
        Sentry.captureException(e);
      }
    })();
  }, [id]);

  const requestAiTranslation = async (name: string, description: string): Promise<AiTranslationResult> => {
    try {
      const response = await translateService.translateItem({
        locationId: id,
        itemName: name,
        itemDescription: description,
      });
      const result: AiTranslationResult = {};
      (response as any[]).forEach((tr) => {
        const languageId = resolveLanguageId(tr.language);
        if (languageId && languageId !== locationLanguage) {
          result[languageId] = {
            name: tr.translatedName ?? '',
            description: tr.translatedDescription ?? '',
          };
        }
      });
      notify(t('notifications.translationCompleted'), { type: 'success' });
      return result;
    } catch (error: any) {
      notify(error.response?.data?.error, { type: 'error' });
      console.error(error.response?.data?.error);
      Sentry.captureException(error);
      throw error;
    }
  };

  React.useEffect(() => {
    if (!id) return;

    (async () => {
      setProductsLoading(true);
      try {
        const res = await axiosInstance.get('/menu-item/get/list', {
          params: {
            /* Nie `backendLanguage` (jezyk panelu), tylko jezyk edytowanego
               tlumaczenia — endpoint zwraca nazwy juz przetlumaczone, wiec zmiana
               selecta przelacza tez nazwy powiazanych produktow. */
            language: formLanguage,
            restaurantId: id,
          },
        });

        // The endpoint returns { restaurantImageUrl, menuItems: [...] }; older
        // responses returned a bare array, so support both shapes.
        const menuItems: any[] = Array.isArray(res.data)
          ? res.data
          : (res.data?.menuItems ?? []);

        const products: LinkedProduct[] = menuItems
          .map((item: any) => ({
            id: item.id,
            name: item.name,
          }))
          .filter((p: LinkedProduct) => !isEditMode || p.id !== Number(itemId));

        setAvailableProducts(products);
      } catch (e) {
        console.error('Failed to load products for linking', e);
        Sentry.captureException(e);
      } finally {
        setProductsLoading(false);
      }
    })();
  }, [id, isEditMode, itemId, formLanguage]);

  React.useEffect(() => {
    if (isEditMode) {
      setIsLoading(true);
      (async () => {
        try {
          const data = await restaurantItemService.getItems(Number(itemId), backendLanguage);
          setData(data);
          setGroups(groupVariants(data.variants || [], makeGroupKey));
          console.log('Fetched item data:', data);
          setPreviewUrl(data.imageUrl);
          // Top-level name/description to tekst oryginalny (w jezyku lokalizacji),
          // a `translations` zawiera wszystkie jezyki, kluczowane id ("ENG").
          const loaded: TranslationsMap = {};
          (data.translations || []).forEach((tr: any) => {
            const languageId = resolveLanguageId(tr.language);
            if (!languageId) return;
            const name = toFieldTranslation(tr.translatedName, tr.nameSource);
            const description = toFieldTranslation(tr.translatedDescription, tr.descriptionSource);
            loaded[languageId] = {
              ...(name ? { name } : {}),
              ...(description ? { description } : {}),
            };
          });
          setTranslations(loaded);
          setAutoTranslate(data.autoTranslate !== false);

          setIsLoading(false);
        } catch (error: any) {
          notify(error.response.data.error, { type: 'error' });
          console.error(error.response.data.error);
          setIsLoading(false);
          Sentry.captureException(error);
        }
      })();
    }
  }, [isEditMode, itemId, backendLanguage]);

  React.useEffect(() => {
    if (data && locationLanguage && Object.keys(translations).length > 0 && lastSnapshot === null) {
      setLastSnapshot({
        language: locationLanguage,
        values: { name: data.name ?? '', description: data.description ?? '' },
      });
    }
  }, [data, locationLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  const handleFile = (file: File) => {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      notify(t('notifications.invalidImageFormat'), { type: 'error' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setRawImageSrc(reader.result as string);
      setCropDialogOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = (croppedBlob: Blob) => {
    setFileBlob(croppedBlob);
    setPreviewUrl(URL.createObjectURL(croppedBlob));
    setCropDialogOpen(false);
  };

  const handleCropCancel = () => {
    setCropDialogOpen(false);
    if (!fileBlob) {
      setRawImageSrc(null);
    }
  };

  const handleRemove = () => {
    setFileBlob(null);
    setPreviewUrl(null);
    setRawImageSrc(null);
  };

  const openAddGroup = () => {
    setEditingGroup(null);
    setVariantDialogOpen(true);
  };

  const openEditGroup = (group: VariantGroup) => {
    setEditingGroup(group);
    setVariantDialogOpen(true);
  };

  // Szablony wariantow tej lokalizacji — szybki start w dialogu grupy.
  const { data: variantTemplates = [], refetch: refetchTemplates } = useGetList<VariantTemplate>(
    'variantTemplates',
    {
      filter: { locationId: Number(id) },
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'name', order: 'ASC' },
    },
    { enabled: Boolean(id) },
  );
  const [createTemplate] = useCreate();
  const [deleteTemplate] = useDelete();

  // Grupa jest "zapisana jako szablon", jesli w szablonach lokalizacji jest taki
  // o identycznej zawartosci — dzieki temu stan widac tez po ponownym wejsciu,
  // a znika sam, gdy grupa zostanie zmieniona.
  const templatesBySignature = React.useMemo(() => {
    const map = new Map<string, VariantTemplate[]>();
    for (const tpl of variantTemplates) {
      const sig = variantContentSignature(tpl);
      map.set(sig, [...(map.get(sig) ?? []), tpl]);
    }
    return map;
  }, [variantTemplates]);
  // Grupy, dla ktorych trwa zapis/usuwanie szablonu, z docelowym stanem — ikonka
  // przelacza sie od razu, a kolejne klikniecia sa ignorowane do konca operacji.
  const [pendingTemplate, setPendingTemplate] = React.useState<Map<string, boolean>>(new Map());
  const setPending = (key: string, target: boolean | null) =>
    setPendingTemplate((prev) => {
      const next = new Map(prev);
      if (target === null) next.delete(key);
      else next.set(key, target);
      return next;
    });
  const isGroupSavedAsTemplate = (group: VariantGroup) =>
    pendingTemplate.get(group.key) ?? templatesBySignature.has(variantContentSignature(group));

  const removeGroupTemplate = async (group: VariantGroup) => {
    const matching = templatesBySignature.get(variantContentSignature(group)) ?? [];
    if (matching.length === 0) return;
    setPending(group.key, false);
    try {
      for (const tpl of matching) {
        await deleteTemplate('variantTemplates', { id: tpl.id, previousData: tpl }, { returnPromise: true });
      }
      await refetchTemplates();
      notify(t('variantTemplates.notifications.removedFromGroup', { name: group.name }), { type: 'success' });
    } catch (error: any) {
      notify(error?.body?.error ?? error?.message, { type: 'error' });
      Sentry.captureException(error);
    } finally {
      setPending(group.key, null);
    }
  };

  const toggleGroupTemplate = (group: VariantGroup) => {
    if (pendingTemplate.has(group.key)) return;
    if (isGroupSavedAsTemplate(group)) removeGroupTemplate(group);
    else saveGroupAsTemplate(group);
  };

  const saveGroupAsTemplate = async (group: VariantGroup) => {
    if (pendingTemplate.has(group.key) || isGroupSavedAsTemplate(group)) return;
    setPending(group.key, true);
    try {
      await createTemplate(
        'variantTemplates',
        {
          data: {
            locationId: Number(id),
            name: group.name,
            isSingleChoice: group.isSingleChoice,
            isRequired: group.isRequired,
            items: group.items,
          },
        },
        { returnPromise: true },
      );
      await refetchTemplates();
      notify(t('variantTemplates.notifications.savedFromGroup', { name: group.name }), { type: 'success' });
    } catch (error: any) {
      notify(error?.body?.error ?? error?.message, { type: 'error' });
      Sentry.captureException(error);
    } finally {
      setPending(group.key, null);
    }
  };

  const handleSaveGroup = (group: VariantGroup, { saveAsTemplate }: VariantGroupSaveOptions) => {
    setGroups((prev) => {
      const exists = prev.some((g) => g.key === group.key);
      return exists ? prev.map((g) => (g.key === group.key ? group : g)) : [...prev, group];
    });
    if (saveAsTemplate) saveGroupAsTemplate(group);
  };

  const handleDeleteGroup = (key: string) => {
    setGroups((prev) => prev.filter((g) => g.key !== key));
  };

  const variantGroupSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  const handleVariantGroupDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setGroups((prev) => {
      const oldIndex = prev.findIndex((g) => g.key === active.id);
      const newIndex = prev.findIndex((g) => g.key === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const weekDayLabels: Record<string, string> = {
    SUNDAY: t('enums.weekDay.SUNDAY'),
    MONDAY: t('enums.weekDay.MONDAY'),
    TUESDAY: t('enums.weekDay.TUESDAY'),
    WEDNESDAY: t('enums.weekDay.WEDNESDAY'),
    THURSDAY: t('enums.weekDay.THURSDAY'),
    FRIDAY: t('enums.weekDay.FRIDAY'),
    SATURDAY: t('enums.weekDay.SATURDAY'),
  };

  const days = Object.keys(WeekDay).map((id) => ({
    id,
    name: weekDayLabels[id] ?? WeekDay[id as keyof typeof WeekDay],
  }));

  const validationSchema = Yup.object({
    name: Yup.string().required(t('validation.nameRequired')),
    category: Yup.string().required(t('validation.required')),
    price: Yup.string().required(t('validation.required')),
    size: Yup.string().required(t('validation.required')),
    description: Yup.string()
      .required(t('validation.descriptionRequired'))
      .max(
        DESCRIPTION_MAX_LENGTH,
        t('validation.descriptionTooLong', { max: DESCRIPTION_MAX_LENGTH })
      ),
    unit: Yup.string().required(t('validation.required')),
  });

  const initialValues = {
    name: '',
    category: '',
    price: '',
    description: '',
    unit: 'PIECES',
    size: '',
    isAvailableForDelivery: true,

    seasonalEnabled: false,
    specificDateEnabled: false,
    startSeason: '',
    endSeason: '',
    openingHours: days.reduce(
      (acc, day) => {
        acc[day.id] = { openingTime: '00:00', closingTime: '00:00' };
        return acc;
      },
      {} as Record<string, { openingTime: string; closingTime: string }>,
    ),
    dietaryRestrictions: {
      spicy: false,
      dairy_free: false,
      gluten_free: false,
      vegan: false,
      vegetarian: false,
      kosher: false,
      halal: false,
    },
  };

  const initialEditValues =
    isEditMode && data
      ? {
          name: data.name ?? '',
          category: data.category.id || '',
          description: data.description ?? '',
          unit: data.unit || '',
          size: data.size,
          price: data.price || '',
          isAvailableForDelivery: data.isAvailableForDelivery !== false,

          /* Zapisany harmonogram wraca do formularza. Wczesniej edycja zawsze startowala
             z wylaczonym harmonogramem, wiec zapis dowolnej zmiany (np. ceny) kasowal
             godziny i zakres dat produktu. */
          seasonalEnabled: (data.weekdaySchedules ?? []).length > 0,
          specificDateEnabled: Boolean(data.dateSchedule?.startDate && data.dateSchedule?.endDate),
          startSeason: toDateInput(data.dateSchedule?.startDate),
          endSeason: toDateInput(data.dateSchedule?.endDate),
          openingHours: days.reduce(
            (acc, day) => {
              const saved = (data.weekdaySchedules ?? []).find((w: any) => w.weekday === day.id);
              acc[day.id] = {
                openingTime: toTimeInput(saved?.startDate) ?? '00:00',
                closingTime: toTimeInput(saved?.endDate) ?? '00:00',
              };
              return acc;
            },
            {} as Record<string, { openingTime: string; closingTime: string }>,
          ),
          dietaryRestrictions: {
            spicy: data.dietaryRestrictions.some(
              (dr: any) => dr.dietaryRestrictionType === 'SPICY',
            ),
            dairy_free: data.dietaryRestrictions.some(
              (dr: any) => dr.dietaryRestrictionType === 'DAIRY_FREE',
            ),
            gluten_free: data.dietaryRestrictions.some(
              (dr: any) => dr.dietaryRestrictionType === 'GLUTEN_FREE',
            ),
            vegan: data.dietaryRestrictions.some(
              (dr: any) => dr.dietaryRestrictionType === 'VEGAN',
            ),
            vegetarian: data.dietaryRestrictions.some(
              (dr: any) => dr.dietaryRestrictionType === 'VEGETARIAN',
            ),
            kosher: data.dietaryRestrictions.some(
              (dr: any) => dr.dietaryRestrictionType === 'KOSHER',
            ),
            halal: data.dietaryRestrictions.some(
              (dr: any) => dr.dietaryRestrictionType === 'HALAL',
            ),
          },
        }
      : initialValues;

  const loadCategories = React.useCallback(async () => {
    if (!id) return;

    try {
      const res = await axiosInstance.get('/category/list', {
        params: {
          locationId: id,
          language: backendLanguage,
        },
      });

      // Guard against categories returned without a name (e.g. missing an
      // ENG translation) — they would otherwise render as a blank, selectable
      // option in the dropdown.
      setCategories(res.data.filter((c: any) => c.name?.trim()));
    } catch (e) {
      console.error('Failed to load categories', e);
      Sentry.captureException(e);
    } finally {
      setCategoriesLoaded(true);
    }
  }, [id, backendLanguage]);

  React.useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  /* Bez `availableProducts` w zaleznosciach: efekt i tak go nie uzywa, a odkad lista
     przeladowuje sie przy zmianie jezyka, kazde przelaczenie resetowaloby niezapisany
     wybor powiazanych produktow do stanu z serwera. */
  React.useEffect(() => {
    if (data?.suggestedProducts?.length) {
      setLinkedProducts(
        data.suggestedProducts.map((sp: any) => ({
          id: sp.suggestedId,
          name: sp.name,
        })),
      );
    }
  }, [data]);

  /* Nazwa do wyswietlenia bierze sie ze swiezo pobranej listy (jest w aktualnym
     jezyku); `product.name` zostaje fallbackiem, gdy produktu nie ma juz w liscie —
     np. zostal usuniety albo to wlasnie edytowana pozycja, ktora sama siebie nie
     sugeruje. Celowo bez zapisu do stanu, zeby nie nadpisywac wyboru uzytkownika. */
  const linkedProductName = React.useCallback(
    (product: LinkedProduct) =>
      availableProducts.find((p) => p.id === product.id)?.name ?? product.name,
    [availableProducts],
  );

  const handleSubmit = async (values: typeof initialValues) => {
    setAttemptedSubmit(true);
    setIsSaving(true);

    const formData = new FormData();

    const openingHoursData = Object.entries(values.openingHours).map(
      ([weekday, { openingTime, closingTime }]) => {
        return {
          weekday: weekday,
          startTime: openingTime,
          endTime: closingTime,
        };
      },
    );

    const weekdaySchedule = values.seasonalEnabled ? openingHoursData : [];

    const dietaryRestrictions = Object.entries(values.dietaryRestrictions)
      .filter(([, v]) => v)
      .map(([k]) => k.toUpperCase());

    console.log('dietary: ' + dietaryRestrictions);

    // Sprowadzamy grupy z UI z powrotem do płaskiej listy, jakiej oczekuje
    // backend: nazwa grupy staje się `category` każdego wariantu, a reguły
    // grupy są kopiowane na każdy jej wiersz.
    const mappedVariants = groups.flatMap((g) =>
      g.items.map((it) => ({
        name: it.name,
        size: it.size !== '' && !Number.isNaN(Number(it.size)) ? Number(it.size) : undefined,
        price: Number(it.price),
        category: g.name || undefined,
        isRequired: !!g.isRequired,
        isSingleChoice: g.isSingleChoice !== false,
      })),
    );

    const dateScheduleRequest = values.specificDateEnabled
      ? {
          startDate: values.startSeason,
          endDate: values.endSeason,
        }
      : {
          startDate: '',
          endDate: '',
        };

    /* Pole pominiete (undefined) znika przy JSON.stringify i backend czyta je jako
       "nie ruszaj". Pusty string to co innego — kasuje tlumaczenie tego pola. */
    const translationsPayload = buildTranslationPayload({
      translations,
      fields: [{ key: 'name' }, { key: 'description' }],
      sourceLanguage: locationLanguage,
      sourceValues: { name: values.name, description: values.description },
      lastSnapshot,
    }).map((entry) => ({
      language: entry.language,
      name: entry.fields.name?.text,
      nameSource: entry.fields.name?.source,
      description: entry.fields.description?.text,
      descriptionSource: entry.fields.description?.source,
    }));

    const request = {
      id: isEditMode ? Number(itemId) : undefined,
      name: values.name,
      description: values.description,
      baseSize: Number(values.size),
      basePrice: Number(values.price),
      unit: values.unit,
      categoryId: values.category,
      locationId: Number(id),
      dietaryRestrictions,
      autoTranslate,
      isAvailableForDelivery: values.isAvailableForDelivery,
      translations: translationsPayload,
      variants: mappedVariants,
      weekdaySchedule,
      dateScheduleRequest,
      suggestedProducts: linkedProducts.map((p) => ({ inventoryItemId: p.id })),
    };
    console.log('requestPayload' + JSON.stringify(request));
    console.log('adsfasdf: ' + JSON.stringify(linkedProducts.map((p) => p.id)));

    const jsonBlob = new Blob([JSON.stringify(request)], { type: 'application/json' });
    formData.append('request', jsonBlob);

    if (fileBlob) {
      formData.append('file', fileBlob);
    } else {
      formData.append('file', new Blob());
    }
    try {
      if (isEditMode) {
        await restaurantItemService.updateItem(formData);
        // await restaurantItemService.addSuggestedProduct({
        //     menuItemId: Number(itemId),
        //     suggestedProductIds: linkedProducts.map(p => p.id)
        // });
        notify(t('notifications.itemUpdated'), { type: 'success' });
        // Celowo bez await: tlumaczenie idzie po zapisie i trwa kilka sekund, a nie ma
        // powodu trzymac uzytkownika na formularzu. Wynik dojdzie jako kolejny toast.
        void reportTranslationResult(Number(itemId));
      } else {
        await restaurantItemService.createItem(formData);
        notify(t('notifications.itemCreated'), { type: 'success' });
      }
      setIsSaving(false);
      redirect('/locationsManagement/' + id + '/items');
    } catch (error: any) {
      notify(error.response.data.error, { type: 'error' });
      console.error(error.response.data.error);
      setIsSaving(false);
      Sentry.captureException(error);
    }
  };

  /**
   * Dopytuje backend, czym skonczylo sie tlumaczenie po zapisie, i mowi o tym
   * uzytkownikowi. Gdy zapis niczego nie zaplanowal (komplet tlumaczen byl aktualny),
   * milczy — nie ma o czym informowac.
   */
  const reportTranslationResult = async (menuItemId: number) => {
    try {
      const result = await watchTranslations(() =>
        restaurantItemService.getItems(menuItemId, backendLanguage),
      );

      if (!result.wasPending) return;

      if (result.status === 'FAILED') {
        notify(
          t('notifications.translationsFailed', {
            languages: result.failedLanguages.map((code) => t(`enums.language.${code}`)).join(', '),
          }),
          { type: 'warning' },
        );
      } else if (result.status === 'PENDING') {
        notify(t('notifications.translationsStillRunning'), { type: 'info' });
      } else {
        notify(t('notifications.translationsReady'), { type: 'success' });
      }
    } catch (error) {
      // Sam zapis sie udal — nieudane odpytanie o status nie jest bledem dla uzytkownika.
      console.error('Failed to read translation status', error);
    }
  };

  /* Kosz tylko otwiera potwierdzenie — grupa znika dopiero po zatwierdzeniu.
     Wczesniej kliknięcie usuwalo ją od razu, bez cofniecia, a ikona siedzi tuz
     obok danych wiersza. */
  const [groupToDelete, setGroupToDelete] = React.useState<string | null>(null);

  const closeGroupDeleteConfirm = () => setGroupToDelete(null);

  const confirmGroupDelete = () => {
    if (groupToDelete !== null) {
      handleDeleteGroup(groupToDelete);
    }

    closeGroupDeleteConfirm();
  };

  if (isEditMode && isLoading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Formik
        initialValues={initialEditValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, handleChange, errors, touched, handleBlur }) => (
          <Form>
            <ScrollToFirstError />
            <PageHeader
              back={<FormBackButton />}
              title={
                isEditMode
                  ? t('locationsManagement.createItem.editItem')
                  : t('locationsManagement.createItem.addItem')
              }
            />
            <Box
              sx={{
                px: PAGE_GUTTER,
                py: { xs: 2, md: 4 },
                display: 'flex',
                gap: { xs: 'var(--space-6)', md: 'var(--space-8)' },
                flexDirection: { xs: 'column', md: 'row' },
                ...labelAboveSx,
              }}
            >
              {/* Grouped box: seasonal schedule + product data columns, with
                  dietary restrictions underneath at full width. */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1.5,
                  order: { xs: 1, md: 1 },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: { xs: 'var(--space-6)', md: 'var(--space-8)' },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 0.5,
                      order: { xs: 3, md: 1 },
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={2}
                      sx={{
                        justifyContent: 'space-between',
                        mb: 'var(--space-6)',
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: 'var(--text-sm)',
                          mb: 2,
                          color: 'text.secondary',
                        }}
                      >
                        {t('locationsManagement.createItem.seasonalSchedule')}
                      </Typography>
                      <Switch
                        name="seasonalEnabled"
                        checked={values.seasonalEnabled}
                        onChange={handleChange}
                        color="primary"
                        size="small"
                      />
                    </Stack>
                    <Stack spacing={3}>
                      {days.map((day) => (
                        <Stack
                          key={day.id}
                          direction="row"
                          alignItems="center"
                          spacing={2}
                          justifyContent="space-between"
                        >
                          <Typography
                            sx={{
                              flex: 1,
                              fontSize: 'var(--text-xs)',
                              color: 'text.primary',
                              minWidth: 80,
                            }}
                          >
                            {day.name}
                          </Typography>

                          <TextField
                            type="time"
                            size="small"
                            name={`openingHours.${day.id}.openingTime`}
                            disabled={!values.seasonalEnabled}
                            value={values.openingHours[day.id].openingTime}
                            onChange={handleChange}
                          />
                          <TextField
                            type="time"
                            size="small"
                            name={`openingHours.${day.id}.closingTime`}
                            disabled={!values.seasonalEnabled}
                            value={values.openingHours[day.id].closingTime}
                            onChange={handleChange}
                          />
                        </Stack>
                      ))}
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                        sx={{
                          justifyContent: 'space-between',
                          mb: 'var(--space-6)',
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: 'var(--text-sm)',
                            mb: 2,
                            color: 'text.secondary',
                          }}
                        >
                          {t('locationsManagement.createItem.specificDate')}
                        </Typography>
                        <Switch
                          name="specificDateEnabled"
                          checked={values.specificDateEnabled}
                          onChange={handleChange}
                          color="primary"
                          size="small"
                        />
                      </Stack>
                      <Stack spacing={3} sx={{ mt: '-10px' }}>
                        <TextField
                          disabled={!values.specificDateEnabled}
                          name="startSeason"
                          label={t('locationsManagement.createItem.startSeason')}
                          value={values.startSeason}
                          onChange={handleChange}
                          placeholder="dd.mm.yyyy"
                          type="date"
                          fullWidth
                          size="small"
                          slotProps={{ inputLabel: { shrink: true } }}
                        />

                        <TextField
                          disabled={!values.specificDateEnabled}
                          name="endSeason"
                          label={t('locationsManagement.createItem.endSeason')}
                          value={values.endSeason}
                          onChange={handleChange}
                          placeholder="dd.mm.yyyy"
                          type="date"
                          fullWidth
                          size="small"
                          slotProps={{ inputLabel: { shrink: true } }}
                        />
                      </Stack>
                    </Stack>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      order: { xs: 1, md: 2 },
                    }}
                  >
                    <Stack
                      spacing={2.5}
                      sx={{
                        // 12px + mt 8px sekcji tlumaczen = ten sam odstep (20px) co miedzy polami.
                        mb: 1.5,
                        // Each field reserves a helper-text line for validation
                        // errors. Rendering it in normal flow left a large empty
                        // gap under every input. Take the helper out of flow and
                        // let the Stack spacing set an even rhythm instead; errors
                        // then overlay the gap without shifting the layout.
                        '& .MuiFormHelperText-root': {
                          position: 'absolute',
                          top: '100%',
                          marginTop: '1px',
                        },
                      }}
                    >
                      <FormControl size="small" fullWidth disabled>
                        <InputLabel shrink>{t('locationsManagement.create.language')}</InputLabel>
                        <Select displayEmpty value={locationLanguage}>
                          <MenuItem value="" disabled>
                            {t('locationsManagement.create.language')}
                          </MenuItem>
                          {LANGUAGE.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.name}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText sx={{ color: 'text.secondary' }}>
                          {t('locationsManagement.createItem.languageFromLocation')}
                        </FormHelperText>
                      </FormControl>

                      <TextField
                        name="name"
                        size="small"
                        label={t('locationsManagement.createItem.name')}
                        fullWidth
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={(touched.name || attemptedSubmit) && Boolean(errors.name)}
                        helperText={
                          (touched.name || attemptedSubmit) && errors.name ? errors.name : ' '
                        }
                      />

                      <FormControl
                        size="small"
                        fullWidth
                        error={(touched.category || attemptedSubmit) && Boolean(errors.category)}
                      >
                        <InputLabel>{t('locationsManagement.createItem.category')}</InputLabel>
                        <Select
                          displayEmpty
                          name="category"
                          value={values.category}
                          onBlur={handleBlur}
                          onChange={handleChange}
                        >
                          <MenuItem value="" disabled>
                            {t('locationsManagement.createItem.category')}
                          </MenuItem>
                          {categories.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </MenuItem>
                          ))}
                        </Select>

                        <FormHelperText>
                          {touched.category && errors.category ? errors.category : ' '}
                        </FormHelperText>
                      </FormControl>

                      <TextField
                        name="price"
                        type="number"
                        onWheel={preventNumberInputScroll}
                        size="small"
                        label={t('locationsManagement.createItem.price')}
                        fullWidth
                        value={values.price}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        slotProps={{ htmlInput: { min: 0, step: '0.01', inputMode: 'decimal' } }}
                        error={(touched.price || attemptedSubmit) && Boolean(errors.price)}
                        helperText={touched.price && errors.price ? errors.price : ' '}
                      />

                      <FormControl
                        size="small"
                        fullWidth
                        error={(touched.unit || attemptedSubmit) && Boolean(errors.unit)}
                      >
                        <InputLabel>{t('locationsManagement.createItem.unitType')}</InputLabel>
                        <Select
                          name="unit"
                          value={values.unit}
                          onBlur={handleBlur}
                          onChange={handleChange}
                        >
                          {UNITS.map((u) => (
                            <MenuItem key={u.id} value={u.id}>
                              {t(`enums.unit.${u.id}`, { defaultValue: u.name })}
                            </MenuItem>
                          ))}
                        </Select>

                        <FormHelperText>
                          {(touched.unit || attemptedSubmit) && errors.unit ? errors.unit : ' '}
                        </FormHelperText>
                      </FormControl>
                      <TextField
                        name="size"
                        type="number"
                        onWheel={preventNumberInputScroll}
                        label={t('locationsManagement.createItem.amount')}
                        fullWidth
                        slotProps={{ htmlInput: { min: 0, step: 'any', inputMode: 'decimal' } }}
                        value={values.size}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={(touched.size || attemptedSubmit) && Boolean(errors.size)}
                        helperText={touched.size && errors.size ? errors.size : ' '}
                        sx={{
                          '& .MuiInputBase-root': { padding: 0 },
                        }}
                      />

                      <TextField
                        name="description"
                        label={t('locationsManagement.create.description')}
                        multiline
                        rows={4}
                        fullWidth
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          (touched.description || attemptedSubmit) && Boolean(errors.description)
                        }
                        helperText={
                          (touched.description || attemptedSubmit) && errors.description
                            ? errors.description
                            : `${values.description.length} / ${DESCRIPTION_MAX_LENGTH}`
                        }
                        sx={{
                          '& .MuiInputBase-root': { padding: '10px 14px' },
                          // Licznik znakow zostaje w przeplywie - absolutny (jak w reszcie Stacka) wchodzilby
                          // w odstep pod polem i przyklejal sie do przelacznika ponizej.
                          '&& .MuiFormHelperText-root': { position: 'static' },
                        }}
                      />

                      {/* Wylaczony = produkt tylko w menu QR, aplikacja mobilna go nie pokazuje. */}
                      <Stack
                        component="label"
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{ cursor: 'pointer', width: 'fit-content' }}
                      >
                        <Switch
                          name="isAvailableForDelivery"
                          checked={values.isAvailableForDelivery}
                          onChange={handleChange}
                          color="primary"
                          size="small"
                        />
                        <Box>
                          <Typography sx={{ fontSize: 'var(--text-sm)', color: 'text.primary' }}>
                            {t('locationsManagement.createItem.availableForDelivery')}
                          </Typography>
                          <Typography sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary' }}>
                            {t('locationsManagement.createItem.availableForDeliveryHint')}
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>

                    <TranslationsSection
                      languages={LANGUAGE}
                      sourceLanguage={locationLanguage}
                      fields={[
                        {
                          key: 'name',
                          label: t('locationsManagement.createItem.name'),
                          maxLength: NAME_MAX_LENGTH,
                        },
                        {
                          key: 'description',
                          label: t('locationsManagement.create.description'),
                          maxLength: DESCRIPTION_MAX_LENGTH,
                          multiline: true,
                        },
                      ]}
                      sourceValues={{ name: values.name, description: values.description }}
                      translations={translations}
                      lastSnapshot={lastSnapshot}
                      onApply={(next, snapshot) => {
                        setTranslations(next);
                        setLastSnapshot(snapshot);
                      }}
                      autoTranslate={autoTranslate}
                      onAutoTranslateChange={setAutoTranslate}
                      requestAiTranslation={() => requestAiTranslation(values.name, values.description)}
                    />
                  </Box>
                </Box>

                {/* Dietary restrictions — full width beneath the two columns */}
                <Box
                  sx={{
                    mt: 'var(--space-6)',
                    display: 'flex',
                    gap: 'var(--space-6)',
                    justifyContent: 'start',
                    flexDirection: 'row',
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: 'var(--text-sm)',
                      mb: 2,
                      color: 'text.secondary',
                    }}
                  >
                    {t('locationsManagement.createItem.dietaryRestrictions')}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    /* A wrapping flex row produced ragged, unaligned columns
                       (and left "Halal" orphaned). A grid keeps the switches on
                       a common axis at every width. */
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr 1fr',
                      md: 'repeat(auto-fill, minmax(180px, 1fr))',
                    },
                    columnGap: 'var(--space-4)',
                    rowGap: 'var(--space-2)',
                    alignItems: 'center',
                    mb: 'var(--space-8)',
                  }}
                >
                  {/* Switch stoi tuz przed etykieta, a caly wiersz jest <label>.
                      Wczesniej `space-between` odsuwal switch na prawy brzeg komorki,
                      gdzie wygladal na przypisany do etykiety z nastepnej kolumny. */}
                  {DIETARY_RESTRICTIONS.map(({ key, labelKey }) => (
                    <Stack
                      key={key}
                      component="label"
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      sx={{ cursor: 'pointer', width: 'fit-content', py: '2px' }}
                    >
                      <Switch
                        name={`dietaryRestrictions.${key}`}
                        checked={values.dietaryRestrictions[key]}
                        onChange={handleChange}
                        color="primary"
                        size="small"
                      />
                      <Typography sx={{ fontSize: 'var(--text-sm)', color: 'text.primary' }}>
                        {t(`locationsManagement.createItem.${labelKey}`)}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  gap: 'var(--space-6)',
                  alignItems: 'center',
                  order: { xs: 2, md: 2 },
                  px: { xs: 'var(--space-2)', md: 0 },
                }}
              >
                <Box
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                  sx={{
                    width: '100%',
                    height: { xs: 200, md: 360 },
                    overflow: 'hidden',
                    border: '1px dashed var(--color-secondary)',
                    borderRadius: 'var(--radius-xs)',
                    backgroundColor: 'var(--color-drop-surface)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                    fontSize: 'var(--text-xs)',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'var(--color-drop-surface-active)',
                    },
                  }}
                  onClick={() => document.getElementById('fileInput')?.click()}
                >
                  <input
                    id="fileInput"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    hidden
                    onChange={handleFileSelect}
                  />

                  {previewUrl ? (
                    <Box sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
                      <img
                        src={previewUrl}
                        alt="preview"
                        style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '4px',
                          objectFit: 'cover',
                        }}
                      />
                    </Box>
                  ) : (
                    <>
                      {/* Drag & drop does not exist on a phone — tell touch
                          users what actually works. */}
                      <Typography sx={{ mb: 0.5 }}>
                        {isMobile
                          ? t('locationsManagement.create.tapToChooseFile')
                          : t('locationsManagement.create.dropFileHere')}
                      </Typography>
                    </>
                  )}
                </Box>

                {fileBlob && (
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2 }}>
                    <IconButton
                      icon={<EditIcon />}
                      ariaLabel={t('common.edit', { defaultValue: 'Edit' })}
                      onClick={() => {
                        if (rawImageSrc) {
                          setCropDialogOpen(true);
                        } else {
                          document.getElementById('fileInput')?.click();
                        }
                      }}
                    />

                    <IconButton
                      icon={<DeleteOutlineIcon />}
                      ariaLabel={t('common.delete')}
                      color="var(--color-error)"
                      onClick={handleRemove}
                    />
                  </Stack>
                )}

                <ImageCropDialog
                  open={cropDialogOpen}
                  imageSrc={rawImageSrc}
                  aspect={16 / 9}
                  onClose={handleCropCancel}
                  onCropComplete={handleCropComplete}
                />
                <Box sx={{ width: '100%' }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography
                      sx={{ fontWeight: 600, fontSize: 'var(--text-sm)', color: 'text.secondary' }}
                    >
                      {t('locationsManagement.createItem.variantGroupDialog.variantGroups')}
                    </Typography>
                    <Button
                      type="button"
                      startIcon={<AddIcon />}
                      onClick={openAddGroup}
                      sx={{ textTransform: 'none', color: 'var(--color-primary)' }}
                    >
                      {t('locationsManagement.createItem.variantGroupDialog.addVariantGroup')}
                    </Button>
                  </Stack>

                  {groups.length === 0 ? (
                    <Typography sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary', py: 2 }}>
                      {t('locationsManagement.createItem.variantGroupDialog.noVariantGroups')}
                    </Typography>
                  ) : (
                    <DndContext
                      sensors={variantGroupSensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleVariantGroupDragEnd}
                    >
                      <SortableContext
                        items={groups.map((g) => g.key)}
                        strategy={verticalListSortingStrategy}
                      >
                        <Stack spacing={2}>
                          {groups.map((group) => (
                            <SortableVariantGroupCard
                              key={group.key}
                              group={group}
                              onEdit={openEditGroup}
                              onToggleTemplate={toggleGroupTemplate}
                              isSavedAsTemplate={isGroupSavedAsTemplate(group)}
                              onDelete={(key) => setGroupToDelete(key)}
                            />
                          ))}
                        </Stack>
                      </SortableContext>
                    </DndContext>
                  )}
                </Box>

                <Box sx={{ width: '100%' }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: 'var(--text-sm)',
                      mb: 1,
                      color: 'text.secondary',
                    }}
                  >
                    {t('locationsManagement.createItem.linkedProducts')}
                  </Typography>

                  <Autocomplete
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid var(--color-secondary) !important',
                        borderRadius: 'var(--radius-xs)',
                      },
                      '& .MuiInputBase-input': {
                        border: '0 !important',
                        padding: '12px 12px !important',
                      },
                      '& .MuiInputBase-root': {
                        padding: '0 !important',
                      },
                    }}
                    multiple
                    options={availableProducts}
                    getOptionLabel={(option) => linkedProductName(option)}
                    value={linkedProducts}
                    loading={productsLoading}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onChange={(_, newValue) => setLinkedProducts(newValue)}
                    noOptionsText={t('locationsManagement.createItem.noProductsOptions')}
                    loadingText={t('locationsManagement.createItem.loadingProducts')}
                    renderTags={() => null}
                    renderInput={(params) => {
                      const { InputProps: autocompleteInputProps, ...restParams } = params;
                      return (
                        <TextField
                          {...restParams}
                          placeholder={t('locationsManagement.createItem.searchProducts')}
                          size="small"
                          slotProps={{
                            input: {
                              ...autocompleteInputProps,
                              endAdornment: (
                                <>
                                  {productsLoading ? (
                                    <CircularProgress color="inherit" size={20} />
                                  ) : null}
                                  {autocompleteInputProps.endAdornment}
                                </>
                              ),
                            },
                          }}
                        />
                      );
                    }}
                  />

                  {linkedProducts.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {linkedProducts.map((product) => (
                        <Chip
                          key={product.id}
                          label={linkedProductName(product)}
                          size="small"
                          onDelete={() =>
                            setLinkedProducts((prev) => prev.filter((p) => p.id !== product.id))
                          }
                          sx={{
                            backgroundColor: 'var(--color-primary)',
                            color: '#fff',
                            '& .MuiChip-deleteIcon': {
                              color: '#fff',
                              '&:hover': { color: '#ddd' },
                            },
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>

                <Box
                  sx={{
                    width: '100%',
                    mt: 'var(--space-6)',
                    display: { xs: 'none', md: 'flex' },
                    gap: 'var(--space-8)',
                    justifyContent: 'flex-end',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                  }}
                >
                  <Button
                    variant="outlined"
                    type="button"
                    onClick={() => redirect('/locationsManagement/' + id + '/items')}
                    sx={{
                      borderRadius: 'var(--radius-sm)',
                      borderColor: 'var(--color-error)',
                      color: 'var(--color-error)',
                      transition: 'background-color 200ms ease, border-color 200ms ease',
                      '&:hover': {
                        borderColor: 'var(--color-error-hover)',
                        backgroundColor: 'var(--color-error-soft)',
                        color: 'var(--color-error-hover)',
                      },
                    }}
                  >
                    {t('common.cancel')}
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      borderRadius: 'var(--radius-sm)',
                      backgroundColor: 'var(--color-primary)',
                      color: 'var(--color-text-inverse)',
                      boxShadow: 'none',
                      transition: 'background-color 200ms ease',
                      '&:hover': {
                        backgroundColor: 'var(--color-primary-hover)',
                        color: 'var(--color-text-inverse)',
                      },
                    }}
                  >
                    {isSaving ? (
                      <CircularProgress size={25} sx={{ color: 'white' }} />
                    ) : isEditMode ? (
                      t('common.saveChanges')
                    ) : (
                      t('common.add')
                    )}
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* mobile buttons — visible only below md, desktop version is in the right column */}
            <Box
              sx={{
                px: PAGE_GUTTER,
                pb: 'var(--space-6)',
                display: { xs: 'flex', md: 'none' },
                gap: 'var(--space-3)',
                flexDirection: 'column',
              }}
            >
              <Button
                type="submit"
                variant="contained"
                sx={{
                  borderRadius: 'var(--radius-sm)',
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--color-text-inverse)',
                  boxShadow: 'none',
                  transition: 'background-color 200ms ease',
                  '&:hover': {
                    backgroundColor: 'var(--color-primary-hover)',
                    color: 'var(--color-text-inverse)',
                  },
                }}
              >
                {isSaving ? (
                  <CircularProgress size={25} sx={{ color: 'white' }} />
                ) : isEditMode ? (
                  t('common.saveChanges')
                ) : (
                  t('common.add')
                )}
              </Button>

                          <Button
                variant="text"
                type="button"
                onClick={() => redirect('/locationsManagement/' + id + '/items')}
                sx={{
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--color-text-secondary)',
                  '&:hover': {
                    backgroundColor: 'var(--color-bg-subtle)',
                    color: 'var(--color-text-primary)',
                  },
                }}
              >
                {t('common.cancel')}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>

      {/* A product must belong to a category. When none exist yet, prompt the
          user to create one instead of letting them fill in a form they can't
          submit. */}
      <Dialog
        open={!isEditMode && categoriesLoaded && categories.length === 0 && !categoryDialogOpen}
        onClose={() => redirect('/locationsManagement/' + id + '/items')}
        slotProps={{
          paper: { sx: { borderRadius: 2, border: '1px solid var(--color-secondary)' } },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('locationsManagement.createItem.noCategoriesTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('locationsManagement.createItem.noCategoriesDescription')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => redirect('/locationsManagement/' + id + '/items')}
            sx={{
              borderRadius: 'var(--radius-sm)',
              borderColor: 'var(--color-error)',
              color: 'var(--color-error)',
              '&:hover': {
                borderColor: 'var(--color-error-hover)',
                backgroundColor: 'var(--color-error-soft)',
                color: 'var(--color-error-hover)',
              },
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => setCategoryDialogOpen(true)}
            sx={{
              borderRadius: 'var(--radius-sm)',
              backgroundColor: 'var(--color-primary)',
              color: 'var(--color-text-inverse)',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'var(--color-primary-hover)',
                color: 'var(--color-text-inverse)',
              },
            }}
          >
            {t('locationsManagement.createItem.goToCategories')}
          </Button>
        </DialogActions>
      </Dialog>

      <Confirm
        isOpen={groupToDelete !== null}
        title={t('locationsManagement.createItem.confirmDeletion.confirmDeletionTitle')}
        content={t('locationsManagement.createItem.confirmDeletion.confirmDeletionBody')}
        confirm={t('common.delete')}
        cancel={t('common.cancel')}
        onConfirm={confirmGroupDelete}
        onClose={closeGroupDeleteConfirm}
      />

      <CategoryAddDialog
        open={categoryDialogOpen}
        onClose={() => {
          setCategoryDialogOpen(false);
          // Re-fetch so a category just added fills the dropdown and dismisses
          // the prompt above.
          loadCategories();
        }}
      />

      <VariantGroupDialog
        open={variantDialogOpen}
        onClose={() => setVariantDialogOpen(false)}
        onSave={handleSaveGroup}
        templates={variantTemplates}
        allowSaveAsTemplate
        initialGroup={editingGroup}
        makeKey={makeGroupKey}
      />
    </>
  );
}
